import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useTabNav } from "@/context/TabNavContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bell, AlertTriangle, Clock, ClipboardList, Plane } from "lucide-react";

const KIND = {
  overdue: { cls: "text-destructive", icon: AlertTriangle, label: "Overdue" },
  due: { cls: "text-amber-600", icon: Clock, label: "Due soon" },
  reminder: { cls: "text-sky-600", icon: Bell, label: "Reminder" },
};

export default function NotificationBell() {
  const { requestTab } = useTabNav();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ count: 0, items: [], new_moms: 0, leave_pending: 0, leave_decided: 0, off_next_week_missing: false });
  const go = (tab) => { requestTab(tab); setOpen(false); };

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setData(res.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className="relative h-9 w-9 rounded-lg hover:bg-secondary flex items-center justify-center transition-colors" data-testid="notification-bell">
          <Bell className="h-5 w-5 text-muted-foreground" />
          {data.count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">
              {data.count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Notifications</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {data.new_moms > 0 && (
          <button className="w-full text-left px-2 py-2 text-sm flex items-center gap-2 text-foreground hover:bg-secondary rounded-md" data-testid="notif-new-moms" onClick={() => go("mom")}>
            <ClipboardList className="h-4 w-4 text-primary" />
            {data.new_moms} new MOM{data.new_moms > 1 ? "s" : ""} you were tagged in
          </button>
        )}
        {data.leave_pending > 0 && (
          <button className="w-full text-left px-2 py-2 text-sm flex items-center gap-2 text-foreground hover:bg-secondary rounded-md" data-testid="notif-leave-pending" onClick={() => go("leave")}>
            <Plane className="h-4 w-4 text-amber-600" />
            {data.leave_pending} leave request{data.leave_pending > 1 ? "s" : ""} awaiting your review
          </button>
        )}
        {data.leave_decided > 0 && (
          <button className="w-full text-left px-2 py-2 text-sm flex items-center gap-2 text-foreground hover:bg-secondary rounded-md" data-testid="notif-leave-decided" onClick={() => go("leave")}>
            <Plane className="h-4 w-4 text-emerald-600" />
            {data.leave_decided} leave request{data.leave_decided > 1 ? "s" : ""} you raised got a decision
          </button>
        )}
        {data.off_next_week_missing && (
          <button className="w-full text-left px-2 py-2 text-sm flex items-center gap-2 text-foreground hover:bg-secondary rounded-md" data-testid="notif-off-missing" onClick={() => go("leave")}>
            <Clock className="h-4 w-4 text-amber-600" />
            Set your Off day(s) for next week (due Wednesday)
          </button>
        )}
        {data.items.length === 0 && data.new_moms === 0 && data.leave_pending === 0 && data.leave_decided === 0 && !data.off_next_week_missing && (
          <p className="px-2 py-6 text-sm text-muted-foreground text-center" data-testid="notif-empty">You're all caught up 🎉</p>
        )}
        <div className="max-h-80 overflow-auto">
          {data.items.map((it) => {
            const k = KIND[it.kind] || KIND.reminder;
            const Icon = k.icon;
            return (
              <button key={it.todo_id} className="w-full text-left px-2 py-2 flex items-start gap-2 border-t border-border first:border-t-0 hover:bg-secondary" data-testid={`notif-item-${it.todo_id}`} onClick={() => go("mytodos")}>
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${k.cls}`} />
                <div className="min-w-0">
                  <p className="text-sm leading-tight break-words">{it.text}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    <span className={k.cls}>{k.label}</span>{it.deadline ? ` · ${it.deadline}` : ""} · {it.mom_title}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
